/**
 * Destination preservation for flows that bounce the user through an
 * intermediate screen (sign-in, password reset, partner impersonation).
 *
 * The `redirectTo` value is attacker-reachable: deep links into Voyager are
 * handed to the system browser by the Next-Gen Player desktop app
 * ("Open in Voyager") and can equally be typed by hand or mailed to an
 * operator. Everything here therefore treats it as untrusted input and only
 * ever yields a site-relative path.
 */

export const REDIRECT_TO_PARAM = 'redirectTo';

/** Where to land when no usable destination was preserved. */
export const DEFAULT_REDIRECT_TO = '/';

type PartialLocation = {
  pathname: string;
  search?: string;
  hash?: string;
};

/**
 * Narrows `value` to a safe, site-relative navigation target.
 *
 * Accepts only rooted paths so the parameter cannot be turned into an
 * open redirect:
 * - `//evil.test` and `/\evil.test` are protocol-relative and would leave the
 *   origin, so they are rejected.
 * - Anything carrying a scheme (`https:`, `javascript:`) is rejected.
 * - A bare or relative value (`music/on-demand`, `../x`) is rejected rather
 *   than guessed at.
 *
 * Returns [DEFAULT_REDIRECT_TO] for every rejected input.
 */
export function sanitizeRedirectTo(
  value: string | null | undefined,
  fallback: string = DEFAULT_REDIRECT_TO,
): string {
  if (typeof value !== 'string') return fallback;

  const target = value.trim();
  if (target === '') return fallback;
  // Must be rooted at the origin.
  if (!target.startsWith('/')) return fallback;
  // Reject protocol-relative targets. Browsers also treat a backslash here as
  // a separator, so `/\evil.test` has to go the same way as `//evil.test`.
  if (target.startsWith('//') || target.startsWith('/\\')) return fallback;
  // A control character could be used to smuggle one of the shapes above past
  // the checks once the browser normalises the URL.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(target)) return fallback;

  return target;
}

/**
 * Serialises a location into the value carried by [REDIRECT_TO_PARAM].
 *
 * Includes `search` and `hash`. Dropping them is what previously lost the
 * player's deep-link context at the sign-in gate: the station and library the
 * operator asked for live in the query string, not the pathname.
 */
export function locationToRedirectTo(location: PartialLocation): string {
  const {pathname, search = '', hash = ''} = location;
  return `${pathname}${search}${hash}`;
}

/**
 * Builds `?redirectTo=<encoded>` for appending to an intermediate route.
 *
 * The value must be percent-encoded. Interpolating it raw means a destination
 * that carries its own query string loses every parameter after the first `&`
 * when the intermediate screen reads it back with `useSearchParams`.
 */
export function redirectToQuery(target: string): string {
  return `${REDIRECT_TO_PARAM}=${encodeURIComponent(target)}`;
}
